/**
 * ImageGallery - Full-featured image gallery with thumbnails, lightbox, and navigation.
 */

import React, { useState, useCallback, useEffect } from 'react';
import APIAgent from '../../agents/apiAgent';
import useSwipeNavigation from '../../hooks/useSwipeNavigation';
import './ImageGallery.css';

const ImageGallery = ({ images = [], onDeleteImage, canManage = false }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const resolveUrl = (src) => APIAgent.getImageUrl(src);
  const hasImages = images.length > 0;

  const goTo = useCallback((idx) => {
    if (images.length === 0) return;
    setActiveIndex(((idx % images.length) + images.length) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, goPrev, goNext]);

  // Touch swipe mirrors the arrow buttons / arrow keys — it never replaces
  // them, so pointer and keyboard users are unaffected.
  const canNavigate = images.length > 1;
  const { swipeHandlers: mainSwipeHandlers, consumeSwipeClick } = useSwipeNavigation({
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
    enabled: canNavigate,
  });
  const {
    swipeHandlers: lightboxSwipeHandlers,
    consumeSwipeClick: consumeLightboxSwipeClick,
  } = useSwipeNavigation({
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
    enabled: canNavigate,
  });

  // Reset active index if images change
  useEffect(() => {
    if (activeIndex >= images.length) {
      setActiveIndex(Math.max(0, images.length - 1));
    }
  }, [images.length, activeIndex]);

  if (!hasImages) {
    return (
      <div className="gallery-empty">
        <div className="gallery-empty-icon">🏠</div>
        <p>No photos yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="gallery">
        {/* Main image */}
        <div className="gallery-main" data-testid="gallery-main" {...mainSwipeHandlers}>
          <img
            className="gallery-main-img"
            src={resolveUrl(images[activeIndex])}
            alt={`Property ${activeIndex + 1} of ${images.length}`}
            onClick={() => {
              // Swallow the synthetic click that trails a swipe, otherwise
              // every swipe would also throw the buyer into the lightbox.
              if (consumeSwipeClick()) return;
              setLightboxOpen(true);
            }}
          />
          <div className="gallery-counter">
            {activeIndex + 1} / {images.length}
          </div>
          {images.length > 1 && (
            <>
              <button className="gallery-nav gallery-nav-prev" onClick={goPrev} aria-label="Previous photo">‹</button>
              <button className="gallery-nav gallery-nav-next" onClick={goNext} aria-label="Next photo">›</button>
            </>
          )}
          <button
            className="gallery-fullscreen-btn"
            onClick={() => setLightboxOpen(true)}
            aria-label="View fullscreen"
          >
            ⛶
          </button>
          {canManage && onDeleteImage && (
            <button
              className="gallery-delete-btn"
              onClick={() => onDeleteImage(activeIndex)}
              aria-label="Delete this photo"
              title="Delete this photo"
            >
              🗑
            </button>
          )}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="gallery-thumbs">
            {images.map((src, idx) => (
              <button
                key={idx}
                className={`gallery-thumb ${idx === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(idx)}
                aria-label={`View photo ${idx + 1}`}
              >
                <img src={resolveUrl(src)} alt={`Thumbnail ${idx + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightboxOpen && (
        <div
          className="lightbox-overlay"
          data-testid="lightbox-overlay"
          // Swipe lives on the OVERLAY, not the content box. `.lightbox-content`
          // shrinks to the image, so with a portrait photo the backdrop bands
          // down either side are where a thumb actually lands — swiping there
          // used to page nothing and let the trailing synthetic click close the
          // viewer. Handling it here makes the whole fullscreen area swipeable.
          onClick={() => {
            // A swipe must not read as a backdrop tap. A genuine tap still closes.
            if (consumeLightboxSwipeClick()) return;
            setLightboxOpen(false);
          }}
          role="dialog"
          aria-label="Photo lightbox"
          {...lightboxSwipeHandlers}
        >
          <div
            className="lightbox-content"
            data-testid="lightbox-content"
            onClick={(e) => {
              // Clicks on the photo never close the viewer. Clear the swipe flag
              // too, so it cannot linger and swallow a later backdrop click on a
              // device that mixes touch and mouse.
              e.stopPropagation();
              consumeLightboxSwipeClick();
            }}
          >
            <img
              className="lightbox-img"
              src={resolveUrl(images[activeIndex])}
              alt={`Property ${activeIndex + 1}`}
            />
            <div className="lightbox-counter">
              {activeIndex + 1} / {images.length}
            </div>
            {images.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-nav-prev" onClick={goPrev} aria-label="Previous photo">‹</button>
                <button className="lightbox-nav lightbox-nav-next" onClick={goNext} aria-label="Next photo">›</button>
              </>
            )}
            <button
              className="lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close lightbox"
            >
              ✕
            </button>
          </div>

          {/* Lightbox thumbnail strip */}
          {images.length > 1 && (
            <div className="lightbox-thumbs" onClick={(e) => e.stopPropagation()}>
              {images.map((src, idx) => (
                <button
                  key={idx}
                  className={`lightbox-thumb ${idx === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(idx)}
                  aria-label={`View photo ${idx + 1}`}
                >
                  <img src={resolveUrl(src)} alt={`Thumbnail ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ImageGallery;
